// Kumadori Club live effects (2x art): searchlight beams into the sky, the LED dance floor and the stage screen's
// equalizer. A fixed handful of sprites plus one Graphics redrawn at 10 Hz, only while the camera is near; "Low effects"
// keeps three still beams and a still floor. The static look is baked into the scene by hd-place-club.ts.
import { rawCanvas } from './canvas';
import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { WORLD } from '../../common/src/data';
import { TILE } from '../../common/src/map';
import { PALETTE } from './theme';
import { hash2 } from '../../common/src/util';

const hexNum = (c: string) => parseInt(c.slice(1), 16);
const COLORS = [PALETTE.pink, PALETTE.cyan, PALETTE.amber].map(hexNum);
const BEAMS = 8, BEAM_LEN = 330, BEAM_W = 26;

/** soft vertical beam: bright at the base (bottom), fading to nothing at the top */
export function beamCanvas(): HTMLCanvasElement {
  const c = rawCanvas(32, 128);
  const g = c.getContext('2d')!;
  for (let y = 0; y < 128; y++) for (let x = 0; x < 32; x++) {
    const side = 1 - Math.abs(x - 15.5) / 16, a = Math.pow(Math.max(0, side), 1.4) * Math.pow(y / 127, 1.6);
    g.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`; g.fillRect(x, y, 1, 1);
  }
  return c;
}

export class ClubFx {
  private root = new Container(); private led = new Graphics();
  private beams: { s: Sprite; ph: number; sp: number; base: number }[] = [];
  private halo: Sprite; private lastStep = -1; private cx: number; private cy: number;
  constructor(parent: Container, glow: Texture, beam: Texture) {
    const c = WORLD.club, z = c.zone;
    this.cx = (z.x + z.w / 2) * TILE; this.cy = (z.y + z.h / 2) * TILE;
    this.root.visible = false;
    this.halo = new Sprite(glow); this.halo.anchor.set(0.5); this.halo.x = this.cx; this.halo.y = this.cy; this.halo.width = 620; this.halo.height = 300; this.halo.blendMode = 'add';
    this.root.addChild(this.halo, this.led);
    // emitters on the north wall (shining over the whole block), two more at the corners
    const ex = [213, 220, 227, 234, 208.5, 239.5, 217, 231];
    for (let i = 0; i < BEAMS; i++) {
      const s = new Sprite(beam); s.anchor.set(0.5, 1); s.blendMode = 'add'; s.width = BEAM_W; s.height = BEAM_LEN;
      s.x = ex[i] * TILE; s.y = (z.y - 0.4) * TILE; s.tint = COLORS[i % 3]; s.alpha = 0.5;
      this.beams.push({ s, ph: hash2(i, 5) * 6.28, sp: 0.5 + hash2(i, 9) * 0.6, base: (i - 3.5) * 0.12 });
      this.root.addChild(s);
    }
    parent.addChild(this.root);
  }
  update(t: number, cam: { x: number; y: number; zoom: number }, vw: number, vh: number, low: boolean) {
    const near = Math.abs(cam.x - this.cx) < vw / 2 / cam.zoom + 330 && cam.y > this.cy - 330 - vh / 2 / cam.zoom && cam.y < this.cy + vh / 2 / cam.zoom + 160;
    this.root.visible = near; if (!near) return;
    const beat = Math.pow(Math.max(0, Math.sin(t * Math.PI * 4)), 6); // 120 bpm kick
    this.halo.alpha = low ? 0.1 : 0.1 + 0.09 * beat;
    this.beams.forEach((b, i) => {
      b.s.visible = !low || i % 3 === 0;
      b.s.rotation = low ? b.base : b.base + Math.sin(t * b.sp + b.ph) * 0.5;
      b.s.alpha = low ? 0.36 : 0.38 + 0.16 * beat;
      b.s.tint = COLORS[(i + (low ? 0 : Math.floor(t / 4))) % 3];
    });
    const step = low ? 0 : Math.floor(t * 10);
    if (step === this.lastStep) return; this.lastStep = step;
    const g = this.led.clear(), d = WORLD.club.dance, s = WORLD.club.stage;
    // LED floor: 8 px cells, colour rings travelling out from the centre
    const cw = d.w * 2, ch = d.h * 2, x0 = d.x * TILE, y0 = d.y * TILE;
    for (let j = 0; j < ch; j++) for (let i = 0; i < cw; i++) {
      const dist = Math.hypot(i - (cw - 1) / 2, (j - (ch - 1) / 2) * 1.5), k = ((dist * 0.7 - t * 3) % 3 + 3) % 3;
      const lit = k < 1.1 ? 1 : k < 1.8 ? 0.45 : 0.12;
      g.rect(x0 + i * 8 + 1, y0 + j * 8 + 1, 6, 6).fill({ color: COLORS[(Math.floor(dist * 0.7 - t * 3 + 30) % 3 + 3) % 3], alpha: 0.5 * lit + 0.1 * beat * lit });
    }
    // stage screen equalizer (the screen is the north wall above the stage)
    const bars = s.w * 2, sx = s.x * TILE, sy = WORLD.club.block.y * TILE + 14;
    for (let i = 0; i < bars; i++) {
      const h = (0.25 + 0.75 * Math.abs(Math.sin(t * 3 + i * 0.7) * Math.sin(t * 1.7 + i * 0.3))) * (low ? 0.5 : 1) * 5;
      g.rect(sx + i * 8 + 1, sy - h, 6, h).fill({ color: h > 3.5 ? COLORS[0] : COLORS[1], alpha: 0.75 });
    }
  }
}